<svelte:options
  customElement={{
    tag: "oc-toggletip-v1",
    /*                                            */
    extend: window.__components.extend(),
    props: {
      position: { type: "String" },
      visible: { type: "Boolean", reflect: true },
      headline: { type: "String" },
      iconType: { type: "String", attribute: "icon-type" },
      headlineLevel: { type: "Number", attribute: "headline-level" },
    },
  }}
/>

<script lang="ts">
  import { useEventDispatcher } from "@otto-ec/otto-components-utils/use/event-dispatcher";
  import { useSlots } from "../../../common/utils/useSlots.svelte";
  import { useTooltipPosition } from "../../../common/utils/useTooltipPosition";
  import type { Events, Props } from "./ToggletipV1.types";

  let {
    position,
    visible = false,
    headline,
    iconType,
    headlineLevel = undefined,
  }: Props = $props();

  const Host = $host();
  const dispatch = useEventDispatcher<Events>(Host);

  const slots = useSlots(Host);

  const headlineElement = $derived(headlineLevel ? `h${headlineLevel}` : "div");

  let closeButton = $state<HTMLElement>();
  let element = $state<HTMLElement>();
  /*                                                                     */
  let focusReturn: HTMLElement | null = null;

  const pos = useTooltipPosition();

  $effect(() => {
    pos.reposition(element!, position);
  });

  function open(target: EventTarget | null, manageFocus: boolean): void {
    if (visible) return;
    if (!dispatch("oc-open", { headline, position, iconType })) return;
    visible = true;

    pos.reposition(element!, position);

    if (!manageFocus) return;
    if (target instanceof HTMLElement) focusReturn = target;
    setTimeout(() => closeButton?.focus());
  }

  function close(manageFocus: boolean): void {
    if (!visible) return;
    if (!dispatch("oc-close", { headline, position, iconType })) return;
    visible = false;

    if (manageFocus) focusReturn?.focus();
    focusReturn = null;
  }

  function onToggleClick(evt: MouseEvent): void {
    const manageFocus = evt.detail === 0;
    if (visible) return close(manageFocus);
    open(evt.composedPath()[0], manageFocus);
  }

  function onCloseClick(evt: MouseEvent): void {
    close(evt.detail === 0);
  }

  function onContentKeyup(evt: KeyboardEvent): void {
    if (evt.key === "Escape") close(true);
  }

  let closeOnClick = true;

  function onBoxClick(): void {
    closeOnClick = false;
  }

  /*                                   */
  const onDocumentClick = (): void => {
    if (closeOnClick) close(false);
    closeOnClick = true;
  };
</script>

<svelte:window onresize={() => close(true)} />
<svelte:document onclick={onDocumentClick} />

<!-- svelte-ignore a11y_click_events_have_key_events -->
<!-- svelte-ignore a11y_no_static_element_interactions -->
<div
  class="toggletip-box"
  data-position-v={$pos.vertical}
  data-position-h={$pos.horizontal}
  onclick={onBoxClick}
>
  <div class="activator" onclick={onToggleClick}><slot /></div>
  <div
    class="toggletip-anchor"
    class:hidden={!visible}
    class:positioning={$pos.positioning}
    aria-hidden={!visible}
    inert={!visible}
  >
    <span class="arrow"></span>
    <!-- TODO: make me accessible -->
    <!-- svelte-ignore a11y_interactive_supports_focus -->
    <div
      class="body"
      bind:this={element}
      role="dialog"
      aria-labelledby={headline ? "toggletip-headline" : "toggletip-content"}
      aria-describedby={headline ? "toggletip-content" : undefined}
      onkeyup={onContentKeyup}
    >
      <div class="main">
        {#if iconType}
          <div class="icon-wrapper"><oc-icon-v1 type={iconType} color="#fff"></oc-icon-v1></div>
        {/if}
        <div class="inline-wrapper">
          <span class="close-button-wrapper">
            <oc-icon-button-v2
              class="close-button"
              variant="inverted-transparent"
              icon="close"
              oc-aria-label="Schließen"
              bind:this={closeButton}
              onclick={onCloseClick}
            ></oc-icon-button-v2></span
          >
          <span class="content">
            {#if headline}
              <svelte:element this={headlineElement} id="toggletip-headline" class="headline">
                {headline}
              </svelte:element>
            {/if}
            <slot name="toggletip-content" id="toggletip-content" />
          </span>
        </div>
      </div>
      {#if slots["toggletip-actions"]}
        <div class="actions">
          <slot name="toggletip-actions" />
        </div>
      {/if}
    </div>
  </div>
</div>

<style lang="scss" global>
  @use "@otto-ec/design-tokens/component" as tokens;

  :host {
    display: inline-block;
    line-height: 0;
  }

  $verticalOffset: calc(
    tokens.$oc-component-toggletip-context-spacing + tokens.$oc-component-toggletip-arrow-height
  );

  .toggletip-box {
    line-height: 0;
    display: flex;
    flex-direction: column;
    align-items: center;

    &[data-position-v="bottom"] {
      flex-direction: column;

      .toggletip-anchor {
        justify-content: flex-end;
      }

      .arrow {
        transform: translateY($verticalOffset);
      }

      .body {
        transform: translateY(calc(100% + $verticalOffset));
      }
    }

    &[data-position-v="top"] {
      flex-direction: column-reverse;

      .toggletip-anchor {
        justify-content: flex-start;
      }

      .arrow {
        transform: rotate(180deg) translateY($verticalOffset);
      }

      .body {
        transform: translateY(calc(-100% - $verticalOffset));
      }
    }

    &[data-position-h="left"] {
      .body {
        left: tokens.$oc-component-toggletip-frame-spacing;
      }
    }

    &[data-position-h="right"] {
      .body {
        right: tokens.$oc-component-toggletip-frame-spacing;
      }
    }
  }

  .toggletip-anchor {
    display: flex;
    flex-direction: column;
    align-items: center;
    z-index: 1;

    transition: opacity tokens.$oc-component-toggletip-show-hide-transition-duration
      tokens.$oc-component-toggletip-show-hide-transition-easing;

    &.hidden {
      transition:
        opacity tokens.$oc-component-toggletip-show-hide-transition-duration
          tokens.$oc-component-toggletip-show-hide-transition-easing,
        visibility 0ms tokens.$oc-component-toggletip-show-hide-transition-duration;
      visibility: hidden;
      opacity: 0;
    }

    &.positioning {
      opacity: 0;
      /*                                                                                                                       */
      overflow: hidden;
      /*                                                                                                                               */
      position: relative;
    }
  }

  .arrow {
    $seamFixHeight: 1px;

    width: calc(tokens.$oc-component-toggletip-arrow-width + $seamFixHeight * 2);
    height: calc(tokens.$oc-component-toggletip-arrow-height + $seamFixHeight);
    margin-bottom: -$seamFixHeight;
    background-color: tokens.$oc-component-toggletip-background-color;
    clip-path: polygon(0% 100%, 50% 0%, 100% 100%);
    transform-origin: center;
    position: absolute;
  }

  .body {
    position: absolute;
    box-sizing: border-box;
    max-width: min(
      tokens.$oc-component-toggletip-max-width,
      calc(100vw - 2 * tokens.$oc-component-toggletip-frame-spacing)
    );
    background-color: tokens.$oc-component-toggletip-background-color;
    padding: tokens.$oc-component-toggletip-close-button-spacing 8px
      tokens.$oc-component-toggletip-action-spacing-bottom
      tokens.$oc-component-toggletip-text-spacing;
    color: tokens.$oc-component-toggletip-copy-color;
    border-radius: tokens.$oc-component-toggletip-border-radius;
    font: tokens.$oc-component-toggletip-copy-font;
  }

  .main {
    display: flex;
  }

  .icon-wrapper {
    padding-top: tokens.$oc-component-toggletip-gap-y;
    padding-right: tokens.$oc-component-toggletip-gap-x;
  }

  .inline-wrapper {
    display: inline-block;
    width: max-content;
    max-width: 100%;
    padding: tokens.$oc-component-toggletip-gap-y 0 tokens.$oc-component-toggletip-gap-y;
    hyphens: auto;
    overflow-wrap: break-word;
  }

  .headline {
    font: tokens.$oc-component-toggletip-headline-font;
    margin-top: 0;
    margin-bottom: tokens.$oc-component-toggletip-gap-y;
  }

  .content {
    float: none;
  }

  .actions {
    text-align: right;
  }

  .close-button-wrapper {
    min-height: calc(1lh + 2 * tokens.$oc-component-toggletip-gap-y);
    display: inline-flex;
    align-items: center;
    float: right;
    margin: calc(-1 * tokens.$oc-component-toggletip-gap-y) 0
      calc(-1 * tokens.$oc-component-toggletip-gap-y) tokens.$oc-component-toggletip-gap-x;
  }
</style>
